import * as React from "react";
import { createPortal } from "react-dom";
import * as debounce from 'lodash/debounce';
import { Loader } from "@inwink/loader";
import { AppCommandResultGroup } from "./appcommandprompt.results";
import { 
    AppCommandPromptContext, IAppCommandPromptContext, IAppCommandPromptRegistration, commandPriorities
} from "./appcommandpromptcontext";
import { ArrowFocusable } from "./arrowfocus";
import { AppTextLabel, withI18nHelper } from "@inwink/i18n";
import type { Entities } from "@inwink/entities/entities";
import { craftAssetUrl } from "@@services/assetservice";

import './appcommandprompt.less';

export interface IAppCommandPromptProvider {
    name: string;
    search: (search: string) => Promise<IAppCommandPromptResultGroup[]>;
}

export interface IAppCommandPromptResultGroup {
    icon?: string;
    label: string;
    hasMore?: boolean;
    moreLink?: string;
    items: IAppCommandPromptResultItem[];
}

export interface IAppCommandPromptResultItem {
    id: string;
    component?: any;
    contentComponent?: any;
    icon?: string;
    label: string;
    sublabel?: string;
    link: string;
    rawdata?: any;
}

export interface IAppCommandPromptProps {
    //providers: IAppCommandPromptProvider[];
    children?: React.ReactNode;
    i18nHelper?: Entities.i18nHelper;
}

export interface IAppCommandPromptState {
    showCommand: boolean;
    triggeredBy?: string;
    search: string;
    searching: boolean;
    results: IAppCommandPromptResultGroup[];
    registrations: IAppCommandPromptRegistration[];
    commandContext: IAppCommandPromptContext;
}

function getTriggers(registrations: IAppCommandPromptRegistration[]) {
    const newTriggers = [];
    registrations.forEach((r) => {
        if (newTriggers.indexOf(r.trigger) < 0) {
            newTriggers.push(r.trigger);
        }
    });
    return newTriggers;
}

@withI18nHelper()
export class AppCommandPrompt extends React.Component<IAppCommandPromptProps, IAppCommandPromptState> {
    portal?: HTMLElement;

    searchInput = React.createRef<HTMLInputElement>();

    constructor(props) {
        super(props);
        this.state = {
            search: "",
            results: null,
            showCommand: false,
            searching: false,
            registrations: [],
            commandContext: {
                triggers: [],
                isSearcheable: () => {
                    return this.state.registrations.filter((r) => !r.standalone).length > 0;
                },
                defaultKey: () => {
                    return this.defaultKey();
                },
                triggerFor: (key: string) => {
                    if (!key) {
                        key = this.defaultKey();
                    }

                    if (this.state.commandContext.triggers.some((e) => e === key)) {
                        this.showFor(key);
                    } else {
                        console.warn("no command trigger registered for " + key);
                    }
                },
                registerCommands: (commands: IAppCommandPromptRegistration) => {
                    this.setState((currentState) => {
                        const newregistrations = [...currentState.registrations];
                        if (newregistrations.indexOf(commands) < 0) {
                            newregistrations.push(commands);
                        }

                        const newTriggers = getTriggers(newregistrations);

                        return {
                            commandContext: Object.assign({}, currentState.commandContext, { 
                                triggers: newTriggers }),
                            registrations: newregistrations
                        };
                    });
                },
                unregisterCommands: (commands: IAppCommandPromptRegistration) => {
                    this.setState((currentState) => {
                        const newregistrations = [...currentState.registrations];
                        let idx;
                        while ((idx = newregistrations.indexOf(commands)) >= 0) {
                            newregistrations.splice(idx, 1);
                        }
                        return {
                            commandContext: Object.assign({}, currentState.commandContext, { 
                                triggers: getTriggers(newregistrations) }),
                            registrations: newregistrations
                        };
                    });
                }
            }
        };
        this.debouncedTriggerSearch = debounce(this.debouncedTriggerSearch, 600);
        this.portal = document.createElement("div");
        this.portal.className = "appcommandportal";
        document.body.appendChild(this.portal);
    }

    defaultKey() {
        let key: string = null;
        commandPriorities.forEach((pk) => {
            if (!key && this.state.commandContext.triggers.some((e) => e === pk)) {
                key = pk;
            }
        });
        return key;
    }

    componentDidMount(): void {

        window.addEventListener("keydown", this.keyPress);
    }

    componentWillUnmount(): void {
        document.body.removeChild(this.portal);
        this.portal = null;
        window.removeEventListener("keydown", this.keyPress);
    }

    hide() {
        this.setState({ showCommand: false, search: "", results: null, /*triggeredBy: null*/ });
    }

    showFor(key: string) {
        this.setState({ showCommand: true, triggeredBy: key, results: null }, () => {

            setTimeout(() => {
                this.searchInput?.current?.focus();
                if (this.state.search) {
                    this.triggerSearch(this.state.search);
                }
            }, 100);
        });
    }

    keyPress = (arg) => {
        if (arg.key === 'Escape' && this.state.showCommand) {
            arg.preventDefault();
            arg.stopPropagation();

            this.hide();
        }

        if (!this.state.registrations.length) {
            return;
        }

        if (arg.ctrlKey === true && this.state.commandContext.triggers.some((e) => e === arg.key)) {
            arg.preventDefault();
            arg.stopPropagation();
            this.showFor(arg.key);
        }
    };

    searchChanged = (arg: React.FormEvent<HTMLInputElement>) => {
        const text = arg.currentTarget.value;
        this.setState({ search: arg.currentTarget.value });
        this.debouncedTriggerSearch(text);
    };

    debouncedTriggerSearch = (text: string) => {
        this.triggerSearch(text);
    };

    currentSearch: Promise<any>;

    triggerSearch = (text: string) => {
        if (!text) {
            this.setState({ searching: false, results: null });
            this.currentSearch = null;
            return;
        }
        let searchComplete = null;
        this.setState({ searching: true, results: [] });

        const search = new Promise((resolve) => {
            searchComplete = resolve;
        });

        const promiseTriggers: (() => void)[] = [];
        this.state.registrations.forEach((registration) => {
            if (registration.trigger === this.state.triggeredBy) {
                registration.providers.forEach((provider) => {
                    promiseTriggers.push(() => provider.search(text).then((groups) => {
                        if (groups && groups.length) {
                            groups.forEach((group) => {
                                this.appendResult(search, group);
                            });
                        }
                    }, (err) => {
                        console.error("error processing provider " + provider.name, err);
                    }));
                });
            }
        });

        this.currentSearch = search;

        Promise.all(promiseTriggers.map(p => p())).then(() => {
            searchComplete();
            if (this.currentSearch === search) {
                this.setState({ searching: false });
                this.currentSearch = null;
            }
        });
    };

    appendResult(search: Promise<any>, group: IAppCommandPromptResultGroup) {
        if (this.currentSearch === search && group != null && group.items.length) {
            this.setState((currentState) => {
                const newresults = currentState.results || [];
                newresults.push(group);
                return {
                    results: newresults
                };
            });
        }
    }

    cancelPropagation = (arg) => {
        arg.stopPropagation();
    };

    renderResults() {
        if (this.state.results != null) {
            const hasItems = this.state.results.some((g) => g.items.length > 0);
            if (!hasItems && !this.state.searching) {
                return <div className="command-results noresult" onClick={this.cancelPropagation}>
                    <AppTextLabel className="commandmessage" i18n="appcommand.nosearchresult" />
                </div>;
            }

            return <div className="command-results" onClick={this.cancelPropagation}>
                {this.state.results.map((g) => {
                    return <AppCommandResultGroup
                        key={g.label}
                        i18nHelper={this.props.i18nHelper}
                        group={g}
                        itemClicked={() => {
                            this.hide();
                        }}
                    />;
                })}
                {this.state.searching ? <div className="tmploader"><Loader /></div> : null}
            </div>;
        }

        return null;
    }

    renderCommand() {
        const otherOptions = this.state.registrations.filter(
            (r) => !r.standalone && r.trigger !== this.state.triggeredBy
        ).map((r) => r.trigger);

        return <div
            className={"appcommandportal-container " + (this.state.showCommand ? "visible" : "hidden")}
            onClick={(arg) => {
                arg.preventDefault();
                arg.stopPropagation();
                this.hide();
            }}
        >
            <ArrowFocusable
                className={"command-container " + (this.state.searching ? "searching" : "")}
                disabled={!this.state.showCommand || !(this.state.results?.length > 0)}
            >
                <div className="command-input" onClick={this.cancelPropagation}>
                    <h2>
                        <img src={craftAssetUrl("/assets/images/inwinklogosmall.png")} className="logo" />
                        <AppTextLabel className="commandname" i18n={"appcommand.title.by." + this.state.triggeredBy} />
                        <div className="searching-wrapper">
                            <Loader className="small" message="" />
                        </div>
                        <span className="othermenus">
                            {otherOptions.map((o) => {
                                return <div 
                                    key={"item" + o}
                                    title={"Ctrl + " + o.toUpperCase()}
                                    className="otheritem clickable"
                                    onClick={(arg) => {
                                        arg.stopPropagation();
                                        this.showFor(o);
                                    }}
                                >
                                    <AppTextLabel i18n={"appcommand.title.by." + o} /> 
                                    <span className="shortcut">Ctrl + {o.toUpperCase()}</span>
                                </div>;
                            })}
                        </span>
                    </h2>
                    <input
                        ref={this.searchInput}
                        type="search"
                        placeholder={this.props.i18nHelper.translate("appcommand.placeholder.by." + this.state.triggeredBy)}
                        className="focusable"
                        value={this.state.search}
                        onChange={this.searchChanged} />
                </div>
                {this.renderResults()}
            </ArrowFocusable>
            <div className="btnclose clickable" onClick={(arg) => {
                arg.preventDefault();
                this.hide();
            }}>
                <i className="inwink-dialog-cancel" />
            </div>
        </div>;
    }

    render() {
        return <AppCommandPromptContext.Provider value={this.state.commandContext}>
            {createPortal(this.renderCommand(), this.portal)}
            {this.props.children}
        </AppCommandPromptContext.Provider>;
    }
}
